/********************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2016, 2025
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} **/

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import classNames from 'classnames';
import { FormGroup, RadioButton, RadioButtonGroup, Search, Checkbox, Button, Layer } from '@carbon/react';
import { SettingsAdjust, CollapseCategories, ExpandCategories } from '@carbon/react/icons';
import ApiFactory from '../utils/api-factory';
import { getTranslatedExtensions } from '../utils/translate';
import { cleanUpKey, filterTags, filterOperations, highlightCleaner, objectToArray, filterChannels, filterWebhooks } from '../utils/common';
import { apiPath, getCurrentItemFromPath, eventPath } from 'utils/navigation';
import MenuDropdown from './MenuDropdown';
import { useConfig } from 'contexts/config';
import { useTranslation } from 'react-i18next';
import { useApi } from 'contexts/ApiHandler';
import { Link, useLocation } from 'react-router-dom';

const defaultState = {
  selectedApi: null,
  definitionNames: [],
  tagHierarchy: null,
  hasTags: false,
  hasOperationIds: false,
  hasSummaries: false,
  operationsArray: [],
  webhooksArray: [],
  viewBy: 'path',
  groupByTags: false,
  expandedAll: null // null so that setting expand OR collapse will update state
};

const handleNewApi = (api, apiHandler, groupByTags, menuView, nestedTags) => {
  let newState = {
    selectedApi: api.info.title + '_' + api.info.version,
    operationsArray: [],
    webhooksArray: [],
    tagHierarchy: {
      tags: {},
      operations: []
    },
    hasTags: false,
    hasOperationIds: false,
    hasSummaries: false,
    selectedTocItem: null,
    groupByTags: false,
    isSOAP: false
  };

  if (!api.asyncapi) {
    newState = handlePaths(api, apiHandler, newState, groupByTags, nestedTags);
  }

  newState.viewBy = menuView || 'path';
  if (apiHandler.isSOAP(api)) {
    newState.isSOAP = true;
    newState.viewBy = menuView || 'operations';
  }

  let definitions = apiHandler.getSchemas(api);
  newState.definitionNames = definitions ? Object.keys(definitions) : [];

  return newState;
};

const handlePaths = (api, apiHandler, newState, groupByTags, nestedTags) => {
  if (!api.paths) {
    api.paths = {};
  }

  // check if the provided api is 3.1.x
  let isOpenapi3_1 = /^3\.1\..*$/.test(api.openapi);
  if (isOpenapi3_1) {
    Object.keys(api.webhooks).forEach((theEvent) => {
      Object.keys(api.webhooks[theEvent]).forEach((method) => {
        let operation = {
          method,
          event: theEvent
        };
        newState.webhooksArray.push(operation);
      });
    });
  }

  newState.tagHierarchy = apiHandler.getApiTags(api);

  let defaultTagOperations = [];

  Object.keys(api.paths).forEach((thePath) => {
    Object.keys(api.paths[thePath]).forEach((method) => {
      if (['get', 'put', 'post', 'delete', 'options', 'patch', 'head', 'trace'].indexOf(method.toLowerCase()) >= 0) {
        let operation = {
          method,
          path: thePath
        };

        if (api.paths[thePath][method].operationId) {
          operation.operationId = api.paths[thePath][method].operationId;
          newState.hasOperationIds = true;
        }

        if (api.paths[thePath][method].summary) {
          operation.summary = api.paths[thePath][method].summary;
          newState.hasSummaries = true;
        }

        if (api.paths[thePath][method]['x-ibm-languages']) {
          operation['x-ibm-languages'] = api.paths[thePath][method]['x-ibm-languages'];
        }

        if (api.paths[thePath][method].tags) {
          operation.tags = api.paths[thePath][method].tags;
        }

        if (api.paths[thePath][method].deprecated) {
          operation.deprecated = api.paths[thePath][method].deprecated;
        }

        newState.operationsArray.push(operation);

        if (operation.tags) {
          let currentTagHierarchy = newState.tagHierarchy;
          operation.tags.forEach((tag) => {
            if (!currentTagHierarchy.tags[tag]) {
              currentTagHierarchy.tags[tag] = {
                tags: {},
                operations: []
              };
            }

            if (nestedTags) {
              // keep moving down the tag tree
              currentTagHierarchy = currentTagHierarchy.tags[tag];
            } else {
              // this operation has this tag...
              if (currentTagHierarchy.tags[tag].operations.indexOf(operation) === -1) {
                currentTagHierarchy.tags[tag].operations.push(operation);
              }
            }

            if (groupByTags || (api.info && api.info['x-codegen-config'] && api.info['x-codegen-config'].groupMethodsByTag)) {
              newState.groupByTags = true;
            }
          });

          if (nestedTags) {
            // this operation only goes in to it's deepest nested tag
            currentTagHierarchy.operations.push(operation);
          }
        } else {
          defaultTagOperations.push(operation);
        }
      }
    });
  });

  if (newState.tagHierarchy && Object.keys(newState.tagHierarchy.tags).length > 0) {
    newState.hasTags = true;
    if (defaultTagOperations.length > 0) {
      newState.tagHierarchy.tags['default'] = {
        tags: {},
        operations: []
      };
      newState.tagHierarchy.tags['default'].operations = defaultTagOperations;
    }
  } else {
    newState.hasTags = false;
  }

  return newState;
};

const Menu = ({ onSelectedOperationChange }) => {
  const { api, apiHandler, loading, error: apiHandlerError } = useApi();
  const { options, apis, docs } = useConfig();
  const [showFilters, setShowFilter] = useState(false);
  const { t } = useTranslation();
  const [state, updateState] = useState(defaultState);
  const [selectedItem, updateSelectedItem] = useState(null);
  const location = useLocation();
  useEffect(() => {
    updateSelectedItem(getCurrentItemFromPath(location.pathname));
  }, [location.pathname]);

  useEffect(() => {
    if (!loading && !apiHandlerError && api !== {} && apiHandler != null) {
      const newState = handleNewApi(api, apiHandler, options.groupByTags, options.menuView, options.nestedTags);
      updateState((prev) => ({
        ...prev,
        ...newState,
        query: ''
      }));
    }
  }, [api, apiHandler, loading, options.groupByTags, options.menuView, options.nestedTags, apiHandlerError]);

  const getFilteredTags = () => {
    let filteredTags = filterTags(state.tagHierarchy.tags, state.viewBy, state.query);
    return objectToArray(filteredTags);
  };

  const viewbyHandler = (event) => {
    updateState({
      ...state,
      viewBy: event
    });
  };

  const filterHandler = (event) => {
    updateState({
      ...state,
      query: event.target.value
    });
  };

  const toggleTags = () => {
    updateState({
      ...state,
      groupByTags: !state.groupByTags
    });
  };

  const expandAll = () => {
    updateState({
      ...state,
      expandedAll: true
    });
  };

  const collapseAll = () => {
    updateState({
      ...state,
      expandedAll: false
    });
  };

  const renderDocMenu = (docs, options = {}) => {
    const docMenu = docs.map((doc) => {
      const docItem = 'doc_' + cleanUpKey(doc.name);
      const docRoute =
        options.embedded && options.api
          ? `/${apiPath(options.api)}/documentation/${options.parent ? `${options.parent}/` : ''}${doc.name}`
          : `/documentation/${options.parent ? `${options.parent}/` : ''}${doc.name}`;
      const display = (
        <Link
          to={docRoute}
          className={classNames('cds--side-nav__link ', 'truncate', 'toc-api', `toc-navigate-${docItem}`, {
            selected: selectedItem === getCurrentItemFromPath(docRoute)
          })}
        >
          <span className="cds--side-nav__link-text">{getTranslatedExtensions(doc, 'title')}</span>
        </Link>
      );

      if (doc.docs && doc.docs.length > 0) {
        let subMenu = renderDocMenu(doc.docs, { ...options, parent: `${options.parent ? options.parent + '/' : ''}${doc.name}` });

        return (
          <li key={docItem} className={classNames('cds--side-nav__menu-item', 'tocItem', 'docItem', docItem)}>
            {display}
            <ul className="doc-tree--list">{subMenu}</ul>
          </li>
        );
      } else {
        return (
          <li key={docItem} className={classNames('cds--side-nav__menu-item', 'tocItem', 'docItem', docItem)}>
            {display}
          </li>
        );
      }
    });

    return docMenu;
  };

  const getDisplayNameForTag = useCallback(
    (operation) => {
      switch (state.viewBy) {
        case 'summary':
          if (operation.summary) return highlightCleaner(getTranslatedExtensions(operation, 'summary'), state.query);
          else return operation.method.toUpperCase() + ' ' + operation.path;
        case 'path':
          return operation.method.toUpperCase() + ' ' + operation.path;
        case 'operations':
          return highlightCleaner(operation.operationId, state.query);
        default:
          break;
      }
    },
    [state.viewBy, state.query]
  );

  // get the selected operation name
  useEffect(() => {
    if (selectedItem && state.operationsArray.length > 0) {
      const selectedOperation = state.operationsArray.find((op) => {
        const route = `/${apiPath(api)}/operation/${encodeURIComponent(op.path)}/${op.method}`;
        return selectedItem === getCurrentItemFromPath(route);
      });

      if (selectedOperation && onSelectedOperationChange) {
        const displayName = getDisplayNameForTag(selectedOperation);
        onSelectedOperationChange(displayName);
      } else if (onSelectedOperationChange) {
        // if no operation is selected
        onSelectedOperationChange(null);
      }
    }
  }, [selectedItem, state.operationsArray, state.viewBy, state.query, api, onSelectedOperationChange, getDisplayNameForTag]);

  const getMenuItemData = (menuItem) => {
    return menuItem.map((operation) => {
      const route = `/${apiPath(api)}/operation/${encodeURIComponent(operation.path)}/${operation.method}`;
      return {
        displayName: getDisplayNameForTag(operation),
        route,
        selected: selectedItem === getCurrentItemFromPath(route)
      };
    });
  };

  const changeExpandedState = (expanded) => {
    updateState({
      ...state,
      expandedAll: null, // reset so collapse/expand can be clicked again
      expanded
    });
  };

  const getMenuData = (tag) => {
    if (tag.value) {
      tag = tag.value;
    }
    const menuItems = getMenuItemData(tag.operations);
    let nested;
    if (tag.tags) {
      nested = Object.entries(tag.tags).reduce((acc, [key, value]) => {
        acc[key] = getMenuData(value);
        acc[key].onToggle = changeExpandedState;
        return acc;
      }, {});
    }
    return {
      menuItems,
      nested
    };
  };

  const renderGroupedMenu = () => {
    return [
      getFilteredTags().map((tag, index) => {
        const { menuItems, nested } = getMenuData(tag);
        const name = tag.key;
        let selected = false;
        return (
          <li key={tag + '_' + index} className={classNames('cds--side-nav__menu-item', 'tocItem', 'tagListItem')} data-testid={'grouped-menu-item'}>
            <MenuDropdown
              className="menuDropdown"
              selected={selected}
              displayName={name}
              menuItems={menuItems}
              nestedMenus={nested}
              isExpanded={state.expandedAll || selected}
              onToggle={changeExpandedState}
            />
          </li>
        );
      })
    ];
  };

  const renderChannels = (api, query, apiHandler) => {
    const channels = filterChannels(apiHandler.api.channels().all(), query);
    return channels.map((channel) => {
      const channelName = channel.id();
      const itemKey = cleanUpKey('channel_' + channelName);
      const operations = apiHandler.api.operations().filterBy((operation) => {
        return operation.channels().has(channelName);
      });
      const overviewRoute = eventPath(api, channelName, 'overview');
      let dataRoute;
      let menuItems;
      menuItems = operations.map((operation) => {
        const operationType = operation.action();
        dataRoute = eventPath(api, channelName, 'operation/' + (operation.id() ? encodeURIComponent(operation.id()) : operationType));
        return {
          displayName: operation.id() ? `${operation.id()} (${t(operation.action())})` : t(`channel_${operationType}_menu`),
          route: dataRoute,
          selected: selectedItem === getCurrentItemFromPath(dataRoute)
        };
      });
      const expandable = apiHandler.api.operations().all().length > 0;
      return (
        <li className={classNames('tocItem', 'cds--side-nav__menu-item')} key={itemKey}>
          {expandable ? (
            <MenuDropdown
              className="menuDropdown"
              displayName={channelName}
              menuItems={menuItems}
              isExpanded={
                selectedItem === getCurrentItemFromPath(dataRoute) ||
                (selectedItem === getCurrentItemFromPath(overviewRoute) && state.expanded) ||
                state.expandedAll
              }
              onToggle={changeExpandedState}
              selected={selectedItem === getCurrentItemFromPath(overviewRoute)}
              to={overviewRoute}
              highlightedQuery={query}
            />
          ) : (
            <Link
              to={overviewRoute}
              className={classNames('cds--side-nav__link', 'truncate toc-selectable', `toc-navigate-${itemKey}`, {
                selected: selectedItem === getCurrentItemFromPath(overviewRoute)
              })}
              title={channelName}
            >
              <span
                className={classNames('cds--side-nav__link-text', 'menu-item-no-expand')}
                dangerouslySetInnerHTML={{ __html: highlightCleaner(channelName, query) }}
                data-testid="channel-no-dropdown"
              />
            </Link>
          )}
        </li>
      );
    });
  };

  let { definitionNames, hasTags, hasOperationIds, hasSummaries, operationsArray, webhooksArray, viewBy, groupByTags, query, isSOAP } = state;

  let filterOptions = [<RadioButton key="path" className="option" value="path" labelText={t('operations_by_path')} data-testid={'tag-filter-view-by-path'} />];

  if (hasOperationIds) {
    filterOptions.push(
      <RadioButton key="operations" className="option" value="operations" labelText={t('operations_by_name')} data-testid={'tag-filter-view-by-name'} />
    );
  }

  if (hasSummaries) {
    filterOptions.push(
      <RadioButton key="summary" className="option" value="summary" labelText={t('operations_by_summary')} data-testid={'tag-filter-view-by-summary'} />
    );
  }

  if (isSOAP && filterOptions.length > 1) {
    filterOptions.shift();
  }

  const apiCommon = useMemo(() => new ApiFactory(api), [api]);

  // Carbon overflow menu is expecting OverflowItems as children.
  // We've got something custom here so make it a component and do a forward ref to satisfy the API
  const FilterOptions = React.forwardRef(() => {
    return (
      <div className={classNames('menuOptions', showFilters ? '' : 'notRendered')} role="menuitem">
        <FormGroup legendText={t('view_operations_by')}>
          <RadioButtonGroup
            orientation="horizontal"
            className="viewByOptions"
            name="cds-apim--menu-viewby"
            label={t('view_operations_by')}
            onChange={viewbyHandler}
            valueSelected={viewBy}
          >
            {filterOptions}
          </RadioButtonGroup>
        </FormGroup>

        {hasTags && (
          <div className="tagFilterOption">
            <Checkbox
              onChange={toggleTags}
              id="groupByTagsCheck"
              labelText={t('group_by_tags')}
              checked={state.groupByTags}
              data-testid={'tag-filter-tags-option'}
            />
          </div>
        )}
      </div>
    );
  });

  const renderApiMenu = (apiItem, api, apiHandler) => {
    return (
      <>
        {api && api.info.title + '_' + api.info.version === apiItem.info.title + '_' + apiItem.info.version && !apiHandlerError && (
          <ul className={classNames('apiContent')}>
            <li className="searchListItem">
              <Layer>
                <Search className="search-input" onChange={filterHandler} labelText={t('filter')} id="search__input" placeholder={t('filter')} />
              </Layer>
              {(hasTags || hasSummaries || hasOperationIds) && (
                <div className="menuViewChooser" data-floating-menu-container>
                  <div className="menuFilter">
                    <Button
                      hasIconOnly
                      kind="ghost"
                      iconDescription={t('filter_options')}
                      renderIcon={SettingsAdjust}
                      onClick={() => {
                        setShowFilter(!showFilters);
                      }}
                      title={t('filter_options')}
                      tooltipPosition="bottom"
                      tooltipAlignment="end"
                      data-testid="tag-filter-options"
                      size="md"
                    />
                  </div>
                  {groupByTags && (
                    <div>
                      {state.expandedAll ? (
                        <Button
                          hasIconOnly
                          kind="ghost"
                          iconDescription={t('collapse_all')}
                          renderIcon={CollapseCategories}
                          onClick={collapseAll}
                          title={t('collapse_all')}
                          tooltipPosition="bottom"
                          tooltipAlignment="end"
                          data-testid={'collapse-all'}
                        />
                      ) : (
                        <Button
                          hasIconOnly
                          kind="ghost"
                          iconDescription={t('expand_all')}
                          renderIcon={ExpandCategories}
                          onClick={expandAll}
                          title={t('expand_all')}
                          tooltipPosition="bottom"
                          tooltipAlignment="end"
                          data-testid={'expand-all'}
                        />
                      )}
                    </div>
                  )}
                </div>
              )}
            </li>
            <FilterOptions />

            <li className={classNames('cds--side-nav__menu-item', 'tocOverview', 'tocHeader')} data-testid={'overview-menu-item'}>
              <Link
                to={`/${apiPath(apiItem)}/overview`}
                className={classNames(
                  {
                    selected: selectedItem === getCurrentItemFromPath(`/${apiPath(apiItem)}/overview`)
                  },
                  'truncate',
                  'toc-api',
                  'cds--side-nav__link',
                  `toc-navigate-overview_${cleanUpKey(apiItem.info.title + '_' + apiItem.info.version)}`
                )}
              >
                <span className="cds--side-nav__link-text">{t('overview')}</span>
              </Link>
            </li>

            {apiItem['x-embedded-doc'] && renderDocMenu(apiItem['x-embedded-doc'], { embedded: true, api: apiItem })}

            {groupByTags && renderGroupedMenu()}

            {!groupByTags &&
              (viewBy === 'operations' || viewBy === 'summary' || viewBy === 'path') && [
                filterOperations(operationsArray, viewBy, query).map((operation) => {
                  const itemKey = 'operation_' + operation.method + '_' + cleanUpKey(operation.path);
                  const pathTo = `/${apiPath(apiItem)}/operation/${encodeURIComponent(operation.path)}/${operation.method}`;
                  return (
                    <li
                      className={classNames(
                        'tocItem',
                        `toc-op-method-${cleanUpKey(operation.method)}`,
                        `toc-op-path-${cleanUpKey(operation.path)}`,
                        'cds--side-nav__menu-item'
                      )}
                      key={itemKey}
                      data-testid={'operation-menu-items'}
                    >
                      <Link
                        to={pathTo}
                        className={classNames(
                          'cds--side-nav__link',
                          'truncate toc-selectable',
                          `toc-navigate-${itemKey}`,
                          {
                            selected: selectedItem === getCurrentItemFromPath(pathTo)
                          },
                          { deprecated: operation.deprecated }
                        )}
                        title={`${operation.method} ${operation.path}`}
                      >
                        {operation.summary && viewBy === 'summary' && (
                          <span
                            className="cds--side-nav__link-text"
                            dangerouslySetInnerHTML={{ __html: highlightCleaner(getTranslatedExtensions(operation, 'summary'), query) }}
                          />
                        )}
                        {operation.operationId && viewBy === 'operations' && (
                          <span className="cds--side-nav__link-text" dangerouslySetInnerHTML={{ __html: highlightCleaner(operation.operationId, query) }} />
                        )}
                        {((!operation.operationId && viewBy === 'operations') || (!operation.summary && viewBy === 'summary') || viewBy === 'path') && (
                          <span className={classNames('cds--side-nav__link-text')}>
                            <span className={classNames(`operation-method-${operation.method}`)}>{operation.method.toUpperCase()}</span>
                            <span
                              className={classNames(`operation-path`)}
                              dangerouslySetInnerHTML={{ __html: highlightCleaner(' ' + operation.path, query) }}
                            />
                          </span>
                        )}
                      </Link>
                    </li>
                  );
                })
              ]}
            {apiItem.webhooks &&
              filterWebhooks(webhooksArray, query).map((webhook) => (
                <li
                  className={classNames(
                    'tocItem',
                    `toc-op-method-${cleanUpKey(webhook.event)}`,
                    `toc-op-path-${cleanUpKey(webhook.method)}`,
                    'cds--side-nav__menu-item'
                  )}
                  key={'webhook_' + webhook.event + '_' + cleanUpKey(webhook.method)}
                  data-testid={'webhook-menu-items'}
                >
                  <Link
                    to={`/${apiPath(apiItem)}/webhook/${encodeURIComponent(webhook.event)}`}
                    className={classNames(
                      'cds--side-nav__link',
                      'truncate toc-selectable',
                      `toc-navigate-${'operation_' + webhook.method + '_' + cleanUpKey(webhook.method)}`,
                      {
                        selected: selectedItem === getCurrentItemFromPath(`/${apiPath(apiItem)}/webhook/${webhook.event}`)
                      },
                      'truncate',
                      'toc-api',
                      'cds--side-nav__link',
                      `toc-navigate-overview_${cleanUpKey(apiItem.info.title + '_' + apiItem.info.version)}`
                    )}
                    title={`webhook: ${webhook.event} `}
                  >
                    <span className={classNames('cds--side-nav__link-text')}>
                      <span className={classNames(`operation-method-${webhook.method}`)}>{webhook.method.toUpperCase()}</span>
                      <span className={classNames(`operation-path`)} dangerouslySetInnerHTML={{ __html: highlightCleaner(' ' + webhook.event, query) }} />
                    </span>
                  </Link>
                </li>
              ))}
            {definitionNames.length > 0 && (
              <li key="definitions-title" className="tocItem tocDefinitions tocHeader" data-testid={'definitions-menu-item'}>
                <Link
                  to={`/${apiPath(apiItem)}/definitions`}
                  className={classNames(
                    { selected: selectedItem === getCurrentItemFromPath(`/${apiPath(apiItem)}/definitions`) },
                    'cds--side-nav__link',
                    'truncate',
                    'toc-api',
                    `toc-navigate-definitions_${cleanUpKey(apiItem.info.title + '_' + apiItem.info.version)}`
                  )}
                >
                  <span className="cds--side-nav__link-text">{t('definitions')}</span>
                </Link>
              </li>
            )}
            {apiItem.asyncapi && renderChannels(apiItem, query, apiHandler)}
          </ul>
        )}
      </>
    );
  };

  return (
    <div className="apiconnect-explorer-menu stretch" data-testid="explorer-menu">
      <section aria-label={t('menu')} className="underMainBanner amp-margin">
        <div className="explorer-toc">
          <ul className={classNames('cds--side-nav__items')}>
            {docs && renderDocMenu(docs)}
            {apis.map((apiItem) => {
              return (
                <li
                  key={apiItem.info.title + '_' + apiItem.info.version}
                  data-testid="total-apis-listed"
                  className={classNames(
                    'cds--side-nav__menu-item',
                    'apiMenu',
                    `api-title-${cleanUpKey(apiItem.info.title)}`,
                    `api-version-${cleanUpKey(apiItem.info.version)}`,
                    apiCommon.isSOAP(apiItem) ? 'api-soap' : 'api-rest'
                  )}
                >
                  {apis.length > 1 && (
                    <Link
                      to={`/${apiPath(apiItem)}/overview`}
                      className={classNames(
                        'cds--side-nav__link',
                        'truncate',
                        'toc-api',
                        `toc-navigate-${cleanUpKey(apiItem.info.title + '_' + apiItem.info.version)}`
                      )}
                    >
                      <span className="cds--side-nav__link-text">
                        {getTranslatedExtensions(apiItem.info, 'title')} {apiItem.info.version}
                      </span>
                    </Link>
                  )}
                  {!loading && renderApiMenu(apiItem, api, apiHandler)}
                </li>
              );
            })}
          </ul>
        </div>
      </section>
    </div>
  );
};

export default Menu;
